import { useQuery } from '@tanstack/react-query';
import type { FreshnessState } from '@/types/index';
import { apiFetch } from '@lib/apiClient';

/**
 * Subscribe to current data freshness state.
 * Per spec §11 refresh — banner reflects current state; scheduled reports
 * (Reporting_v4_Spec.md §12) check this before firing.
 *
 * Calls /api/refresh/status which reads /snapshots/{tenant}/freshness.json from Blob.
 * Refetches every 60s; staleTime 30s.
 */
export function useFreshness() {
  return useQuery<FreshnessState>({
    queryKey: ['freshness'],
    queryFn: async () => {
      const r = await apiFetch('/api/refresh/status', {
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`refresh/status ${r.status}`);
      const data = (await r.json()) as Record<string, unknown>;
      // Map server response shape to FreshnessState discriminated union
      const kind = data.kind as FreshnessState['kind'];
      switch (kind) {
        case 'fresh':
          return { kind: 'fresh', snapshotDate: data.snapshotDate as string };
        case 'stale':
          return {
            kind: 'stale',
            snapshotDate: data.snapshotDate as string,
            lastSuccessfulAt: data.lastSuccessfulAt as string,
          };
        case 'failed':
          return {
            kind: 'failed',
            lastSuccessfulAt: (data.lastSuccessfulAt as string) ?? '',
            reason: data.reason as string,
          };
        case 'rolled_back':
          return {
            kind: 'rolled_back',
            rolledBackTo: data.rolledBackTo as string,
            reason: data.reason as string,
          };
        default:
          throw new Error(`Unknown freshness kind: ${kind}`);
      }
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
}
